import React, { useMemo, useState } from "react";
import { ExternalLink, ArrowUpDown, Search } from "lucide-react";

import {
  SENTIMENT_LABELS,
  CONSTRAINT_LABELS,
  sentimentBadgeClasses,
  yesNo,
  truncate,
} from "../lib/format";

const SENTIMENT_FILTERS = [
  { id: "all", label: "All sentiments" },
  { id: "strongly_supportive", label: "Strongly supportive" },
  { id: "supportive_conditional", label: "Supportive, conditional" },
  { id: "supportive_but_constrained", label: "Supportive, constrained by context" },
  { id: "constrained", label: "Constrained" },
  { id: "resistant", label: "Resistant" },
];

export default function WorkshopsTab({ workshops, onSelect }) {
  const [query, setQuery] = useState("");
  const [sentiment, setSentiment] = useState("all");
  const [sdnpOnly, setSdnpOnly] = useState(false);
  const [sortKey, setSortKey] = useState("workshop_name");
  const [sortDir, setSortDir] = useState("asc");

  const rows = useMemo(() => {
    let list = workshops.slice();
    if (sentiment !== "all")
      list = list.filter((w) => w.sentiment === sentiment);
    if (sdnpOnly)
      list = list.filter((w) => w.sdnp_or_planning_constraint_mentioned);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((w) =>
        [w.workshop_name, w.parish_or_town, w.county, ...(w.constraints || []), ...(w.conditions_for_support || [])]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const s = String(av).localeCompare(String(bv), undefined, {
        sensitivity: "base",
      });
      return sortDir === "asc" ? s : -s;
    });
    return list;
  }, [workshops, query, sentiment, sdnpOnly, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  return (
    <div className="space-y-4" data-testid="workshops-tab">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            Workshop explorer
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            One row per workshop. Sortable, filterable and searchable. Click a
            row to open the full detail panel.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
          <input
            data-testid="workshops-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search workshop, parish, constraint, condition…"
            className="w-full border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F]"
          />
        </div>
        <select
          data-testid="workshops-filter-sentiment"
          value={sentiment}
          onChange={(e) => setSentiment(e.target.value)}
          className="border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/30"
        >
          {SENTIMENT_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <label
          className="inline-flex items-center gap-2 text-sm text-gray-700 select-none"
          data-testid="workshops-filter-sdnp-label"
        >
          <input
            type="checkbox"
            data-testid="workshops-filter-sdnp"
            checked={sdnpOnly}
            onChange={(e) => setSdnpOnly(e.target.checked)}
            className="border-gray-300"
          />
          SDNP / planning constraint only
        </label>
        <span
          className="text-xs text-gray-500 font-mono"
          data-testid="workshops-row-count"
        >
          {rows.length} / {workshops.length} rows
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table
          className="fel-table w-full text-sm text-left text-gray-700"
          data-testid="workshops-table"
        >
          <thead className="text-[11px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-200">
            <tr>
              <Th label="Workshop" k="workshop_name" {...{ sortKey, sortDir, toggleSort }} />
              <Th label="Location" k="parish_or_town" {...{ sortKey, sortDir, toggleSort }} />
              <Th label="Sentiment" k="sentiment" {...{ sortKey, sortDir, toggleSort }} />
              <Th label="Band" k="acceptability_band" {...{ sortKey, sortDir, toggleSort }} />
              <Th label="Turbines" k="turbine_count_proposed" {...{ sortKey, sortDir, toggleSort }} />
              <Th label="Size" k="preferred_size" {...{ sortKey, sortDir, toggleSort }} />
              <Th label="Fewer larger?" k="prefers_fewer_larger" {...{ sortKey, sortDir, toggleSort }} align="center" />
              <Th label="Clustered?" k="prefers_clustering" {...{ sortKey, sortDir, toggleSort }} align="center" />
              <Th label="SDNP?" k="sdnp_or_planning_constraint_mentioned" {...{ sortKey, sortDir, toggleSort }} align="center" />
              <th className="px-3 py-3">Key constraint</th>
              <th className="px-3 py-3">Key condition</th>
              <th className="px-3 py-3">PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr
                key={w.id}
                className="border-b border-gray-100 cursor-pointer"
                data-testid={`workshops-row-${w.id}`}
                onClick={() => onSelect(w.id)}
              >
                <td className="px-3 py-3 font-medium text-gray-900">
                  {w.workshop_name}
                </td>
                <td className="px-3 py-3">
                  <div>{w.parish_or_town || "—"}</div>
                  {w.county ? (
                    <div className="text-[11px] text-gray-500">{w.county}</div>
                  ) : null}
                </td>
                <td className="px-3 py-3">
                  <span
                    className={
                      "inline-block px-2 py-0.5 text-[11px] font-medium " +
                      sentimentBadgeClasses(w.sentiment)
                    }
                  >
                    {SENTIMENT_LABELS[w.sentiment] || "—"}
                  </span>
                </td>
                <td className="px-3 py-3 capitalize">
                  {w.acceptability_band || "—"}
                </td>
                <td className="px-3 py-3 font-mono">
                  {w.turbine_count_proposed || "—"}
                </td>
                <td className="px-3 py-3 capitalize">
                  {w.preferred_size || "—"}
                </td>
                <td className="px-3 py-3 text-center">
                  {yesNo(w.prefers_fewer_larger)}
                </td>
                <td className="px-3 py-3 text-center">
                  {yesNo(w.prefers_clustering)}
                </td>
                <td className="px-3 py-3 text-center">
                  {yesNo(w.sdnp_or_planning_constraint_mentioned)}
                </td>
                <td className="px-3 py-3 text-gray-700">
                  {truncate(
                    (w.constraints || [])
                      .map((c) => CONSTRAINT_LABELS[c] || c)
                      .join(", "),
                    60
                  ) || "—"}
                </td>
                <td className="px-3 py-3 text-gray-700">
                  {truncate((w.conditions_for_support || [])[0], 60) || "—"}
                </td>
                <td className="px-3 py-3">
                  {w.source_url ? (
                    <a
                      href={w.source_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[#2F4F4F] hover:underline"
                      data-testid={`pdf-link-${w.id}`}
                    >
                      PDF <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-10 text-center text-sm text-gray-500"
                >
                  No workshops match the current filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ label, k, sortKey, sortDir, toggleSort, align }) {
  const active = sortKey === k;
  return (
    <th
      scope="col"
      className={
        "px-3 py-3 whitespace-nowrap " + (align === "center" ? "text-center" : "")
      }
    >
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={
          "inline-flex items-center gap-1 hover:text-gray-900 " +
          (active ? "text-gray-900" : "text-gray-500")
        }
        data-testid={`sort-${k}`}
      >
        {label}
        <ArrowUpDown className="h-3 w-3 opacity-60" />
        {active ? (
          <span className="text-[10px] font-mono">
            {sortDir === "asc" ? "↑" : "↓"}
          </span>
        ) : null}
      </button>
    </th>
  );
}
